export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export function safeApiErrorMessage(text: string, statusText: string): string {
  const compact = text.replace(/\s+/g, ' ').trim();
  if (!compact) return statusText || 'The service is temporarily unavailable.';
  if (/<!doctype html|<html[\s>]/i.test(compact)) {
    return 'The service is temporarily unavailable. Please try again.';
  }
  return compact.length <= 300 ? compact : `${compact.slice(0, 297)}...`;
}

export async function api<T = any>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin' };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  const text = await res.text();
  let data: any = null;
  if (text) {
    try { data = JSON.parse(text); } catch {
      data = { message: safeApiErrorMessage(text, res.statusText) };
    }
  }
  if (!res.ok) {
    throw new ApiError(data?.message ?? res.statusText, res.status, data?.error);
  }
  return data as T;
}

export const fmt = {
  /** Renders an instant in the student's timezone so parents see what the child sees. */
  dateTime(iso?: string | null, tz?: string): string {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium', timeStyle: 'short', timeZone: tz || undefined,
      }).format(new Date(iso));
    } catch {
      return new Date(iso).toLocaleString();
    }
  },
  date(iso?: string | null, tz?: string): string {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: tz || undefined })
        .format(new Date(iso));
    } catch {
      return new Date(iso).toLocaleDateString();
    }
  },
  time(iso?: string | null, tz?: string): string {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat(undefined, { timeStyle: 'short', timeZone: tz || undefined })
        .format(new Date(iso));
    } catch {
      return new Date(iso).toLocaleTimeString();
    }
  },
  clockTime(value?: string | null): string {
    if (!value) return '—';
    const match = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(value);
    if (!match) return value;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return value;
    const instant = new Date(Date.UTC(2000, 0, 1, hour, minute));
    return new Intl.DateTimeFormat(undefined, { timeStyle: 'short', timeZone: 'UTC' }).format(instant);
  },
  ago(iso?: string | null): string {
    if (!iso) return 'never';
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.round(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h} h ago`;
    return `${Math.round(h / 24)} d ago`;
  },
  label(s?: string | null): string {
    if (!s) return '—';
    return s.replace(/_/g, ' ');
  },
};
